import { createAsyncThunk, createSelector, createSlice, } from '@reduxjs/toolkit';
import { sendRegistrationBffRequest } from '../../service/registration/index.js';
import { sendGlobalTrackingThunk } from '../../core/thunks.js';
import { executeCallbackFunction } from '../../tools/callback.js';
import { trackFailedRequest, trackEmailAttemptSuccessfulRequest, } from '../../tracking/index.js';
import { navigateTo } from '../../core/navigation/navigationSlice.js';
import { FORM_TYPES } from '../../types/paramsConfig.js';
import { saveCandidateJustLoggedInCookie } from '../../helpers/cookies.js';
import { redirect } from '../../helpers/redirection.js';
import { twoStepComponentStates } from '../../tracking/helpers/constants.js';
export const initialRegistrationState = {
    email: {
        value: '',
        error: false,
        errorMessage: '',
    },
    password: {
        value: '',
        error: false,
        errorMessage: '',
    },
    isLoading: false,
    keepMeInformed: false,
    errorStatus: null,
    showRegistrationSuccessfulModal: false,
};
export const registrationThunk = createAsyncThunk('registration/registrationRequest', async (_, { rejectWithValue, dispatch, getState }) => {
    const state = getState();
    const email = state.forms.registration.email.value;
    const password = state.forms.registration.password.value;
    const consent = state.forms.registration.keepMeInformed;
    const { callbackFunction, redirectionUrl, disableCvUpload, forceRedirect } = state.core.sourceData;
    const navigationContext = state.core.navigation.navigationContext;
    const brandTrackingType = state.core.siteConfig.loginregistration.brandTrackingType;
    const { activeModalType } = state.wrappers.modal;
    const { activeState } = state.forms.twoStepLoginRegistration;
    const twoStepsModalComponentState = twoStepComponentStates[activeState];
    dispatch(registrationSlice.actions.setErrorStatus(null));
    try {
        const registrationResponse = await sendRegistrationBffRequest({
            email,
            password,
            consent: { thirdPartyMarketingContentEmail: consent },
        });
        let trackingCustomPayload = {
            componentState: `consentThirdPartyMarketingContentEmail_${consent ? 'checked' : 'unchecked'}`,
            eventName: ['candidateLogin', 'candidateAccountCreation'],
            uniqueName: 'emailPasswordAuthentication',
        };
        if (brandTrackingType === 'continental') {
            trackingCustomPayload = {
                eventName: [
                    'candidateLogin',
                    'candidateAccountCreation',
                    'cvMadeSearchable',
                    'CandidateAccountConfirmation',
                ],
                uniqueName: 'emailPasswordAuthentication',
            };
        }
        dispatch(sendGlobalTrackingThunk(trackEmailAttemptSuccessfulRequest, trackingCustomPayload));
        if (callbackFunction) {
            await executeCallbackFunction(callbackFunction);
        }
        if (registrationResponse &&
            navigationContext === 'page' &&
            forceRedirect) {
            redirect(redirectionUrl);
            return;
        }
        if (registrationResponse && navigationContext === 'modal') {
            if (disableCvUpload) {
                redirect(redirectionUrl);
                return;
            }
            dispatch(registrationSlice.actions.setShowRegistrationSuccessfulModal(true));
        }
        else if (registrationResponse) {
            saveCandidateJustLoggedInCookie();
            if (redirectionUrl) {
                redirect(redirectionUrl);
            }
            dispatch(navigateTo(FORM_TYPES.REGISTRATION_SUCCESS));
            return;
        }
        return rejectWithValue(500);
    }
    catch (error) {
        dispatch(sendGlobalTrackingThunk(trackFailedRequest, {
            formFailedAttemptsErrors: 'registrationPasswordDoNotMatch',
            ...(activeModalType === 'twoStepLoginRegistration' && {
                componentState: twoStepsModalComponentState,
            }),
        }));
        return rejectWithValue(error);
    }
});
export const registrationSlice = createSlice({
    name: 'registration',
    initialState: initialRegistrationState,
    reducers: {
        updateEmail: (state, actions) => {
            state.email.value = actions.payload;
        },
        updatePassword: (state, actions) => {
            state.password.value = actions.payload;
        },
        setIsLoading: (state, actions) => {
            state.isLoading = actions.payload;
        },
        updateEmailError: (state, actions) => {
            state.email.error = actions.payload;
        },
        updateEmailErrorMessage: (state, actions) => {
            state.email.errorMessage = actions.payload;
        },
        updatePasswordErrorMessage: (state, actions) => {
            state.password.errorMessage = actions.payload;
        },
        updatePasswordError: (state, actions) => {
            state.password.error = actions.payload;
        },
        clearRegistrationInputs: (state) => {
            state.email.error = false;
            state.email.errorMessage = '';
            state.password.error = false;
            state.password.errorMessage = '';
            state.errorStatus = null;
        },
        toggleKeepMeInformed: (state) => {
            state.keepMeInformed = !state.keepMeInformed;
        },
        setErrorStatus: (state, actions) => {
            state.errorStatus = actions.payload;
        },
        setShowRegistrationSuccessfulModal: (state, actions) => {
            state.showRegistrationSuccessfulModal = actions.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(registrationThunk.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(registrationThunk.fulfilled, (state) => {
            state.isLoading = false;
        });
        builder.addCase(registrationThunk.rejected, (state, { payload }) => {
            state.isLoading = false;
            state.errorStatus = payload;
        });
    },
});
export const { updateEmail, updatePassword, setIsLoading, updateEmailError, updatePasswordError, toggleKeepMeInformed, updateEmailErrorMessage, updatePasswordErrorMessage, clearRegistrationInputs, setShowRegistrationSuccessfulModal, setErrorStatus, } = registrationSlice.actions;
const registrationState = (state) => state.forms.registration;
export const emailSelector = createSelector(registrationState, (state) => state.email);
export const passwordSelector = createSelector(registrationState, ({ password }) => password);
export const isLoadingSelector = createSelector(registrationState, ({ isLoading }) => isLoading);
export const keepMeInformedSelector = createSelector(registrationState, ({ keepMeInformed }) => keepMeInformed);
export const errorStatusSelector = createSelector(registrationState, ({ errorStatus }) => errorStatus);
export const showRegistrationSuccessfulModalSelector = createSelector(registrationState, ({ showRegistrationSuccessfulModal }) => showRegistrationSuccessfulModal);
export default registrationSlice.reducer;
//# sourceMappingURL=registrationSlice.js.map